<template>
    <div
        class="gallery-layout-module gallery-layout-module--carousel"
        :class="mockupTypeClass"
        :style="carouselVars"
    >
        <div class="gallery-carousel__stage">
            <div
                ref="emblaViewport"
                class="gallery-carousel__viewport"
                @load.capture="handleCarouselImageLoad"
            >
                <div class="gallery-carousel__track">
                    <div
                        v-for="carouselItem in carouselItems"
                        :key="carouselItem.key"
                        class="gallery-carousel__slide"
                        :data-original-index="carouselItem.originalIndex"
                    >
                        <slot
                            name="gallery-item"
                            :item="carouselItem.item"
                            :index="carouselItem.originalIndex"
                            :rendered-index="carouselItem.renderedIndex"
                            :scroll-hint-priority="carouselItem.renderedIndex === selectedScrollSnap ? 0 : carouselItem.renderedIndex + 1"
                        />
                    </div>
                </div>
            </div>
            <GalleryArrowButtons
                v-if="section?.controlsPosition === C.SIDES"
                :control-position="controlsPosition"
                @next="scrollNext()"
                @previous="scrollPrevious()"
            />
        </div>

        <div
            class="gallery-footer"
            :class="[{'has-controls': controlsPosition === C.BOTTOM}, {'has-caption': section.showCaptions}]"
        >
            <slot
                v-if="section.showCaptions"
                name="caption"
                :active-item="activeGalleryItem"
                :active-index="activeGalleryItemIndex"
                :align-left="controlsPosition === C.BOTTOM"
            />
            <GalleryControls
                :items-length="carouselItems.length"
                :scroll-snaps="scrollSnaps"
                :selected-scroll-snap="selectedScrollSnap"
                :controls-position="section?.controlsPosition"
                :layout-type="section?.layout"
                @previous="scrollPrevious"
                @next="scrollNext"
                @scroll-to="scrollTo"
            />
        </div>
    </div>
</template>

<script>
import EmblaCarousel from "embla-carousel";
import {GalleryControlsPosition} from "@/scripts/common/enums.js";
import {MockupDeviceType} from "@/scripts/common/mockupStore/mockupStructure.js";
import GalleryControls from "@/scripts/sections/modules/gallery/GalleryControls.vue";
import GalleryArrowButtons from "@/scripts/sections/modules/gallery/GalleryArrowButtons.vue";

export default {
    name: "GalleryCarousel",
    components: {
        GalleryArrowButtons,
        GalleryControls
    },
    props: {
        section: {
            type: Object,
            default: null
        },
        items: {
            type: Array,
            default: () => []
        },
        pendingFocusIndex: {
            type: Number,
            default: null
        },
        isPreview: {
            type: Boolean,
            default: false
        }
    },
    emits: ['gallery-item-focus-applied'],
    data() {
        return {
            emblaApi: null,
            selectedScrollSnap: 0,
            scrollSnaps: [],
            originalImageRatios: {},
            C: GalleryControlsPosition
        }
    },
    mounted() {
        this.initCarousel();
    },
    beforeUnmount() {
        this.destroyCarousel();
    },
    watch: {
        items() {
            this.originalImageRatios = {};
            this.reInitCarousel();
            this.focusPendingItem();
        },
        pendingFocusIndex() {
            this.focusPendingItem();
        },
    },
    computed: {
        activeDeviceType() {
            return this.items[0]?.media?.mockupSettings?.deviceType || null;
        },
        minimumLoopItems() {
            const denseDeviceTypes = [
                MockupDeviceType.MOBILE,
                MockupDeviceType.TABLET
            ];
            return denseDeviceTypes.includes(this.activeDeviceType) ? 12 : 8;
        },
        carouselItems() {
            if (!this.items.length) return [];
            const repeatCount = Math.max(1, Math.ceil(this.minimumLoopItems / this.items.length));
            return Array.from({length: repeatCount}).flatMap((_, repeatIndex) => (
                this.items.map((item, originalIndex) => ({
                    item,
                    originalIndex,
                    renderedIndex: (repeatIndex * this.items.length) + originalIndex,
                    key: `${item.media?.url || originalIndex}-${repeatIndex}-${originalIndex}`
                }))
            ));
        },
        noMockupColumnSpan() {
            const ratioColumns = {
                "1_1": 6,
                "3_4": 5,
                "9_16": 4
            };

            if (this.section?.mediaAspectRatio === "Original") {
                return this.getOriginalAspectRatioColumnSpan(0, this.items[0]?.media);
            }

            return ratioColumns[this.section?.mediaAspectRatio] || 8;
        },
        mockupColumnSpan() {
            const columnsByDevice = {
                [MockupDeviceType.MOBILE]: 3,
                [MockupDeviceType.TABLET]: 4,
                [MockupDeviceType.LAPTOP]: 8,
                [MockupDeviceType.DESKTOP]: 6,
                Monitor: 6
            };
            return columnsByDevice[this.activeDeviceType] || this.noMockupColumnSpan;
        },
        mockupTypeClass() {
            return this.activeDeviceType ? this.activeDeviceType.toLowerCase() : "no-mockup";
        },
        controlsPosition() {
            return this.section?.controlsPosition || this.C.SIDES;
        },
        activeGalleryItemIndex() {
            const activeCarouselItem = this.carouselItems[this.selectedScrollSnap];
            return typeof activeCarouselItem?.originalIndex === "number" ? activeCarouselItem.originalIndex : 0;
        },
        activeGalleryItem() {
            return this.carouselItems[this.selectedScrollSnap]?.item || null;
        },
        carouselVars() {
            return {
                '--gallery-carousel-cols': this.mockupColumnSpan
            };
        }
    },
    methods: {
        initCarousel() {
            if (!this.$refs.emblaViewport || !this.carouselItems.length) return;
            this.emblaApi = EmblaCarousel(this.$refs.emblaViewport, {
                align: "center",
                loop: true,
                slidesToScroll: 1
            });
            this.emblaApi.on("select", this.updateCarouselState);
            this.emblaApi.on("reInit", this.updateCarouselState);
            this.updateCarouselState();
            this.focusPendingItem();
        },
        destroyCarousel() {
            this.emblaApi?.destroy();
            this.emblaApi = null;
        },
        reInitCarousel() {
            this.$nextTick(() => {
                if (!this.emblaApi) {
                    this.initCarousel();
                    return;
                }
                this.emblaApi.reInit();
                this.focusPendingItem();
            });
        },
        focusPendingItem() {
            const targetIndex = this.pendingFocusIndex;
            if (!Number.isInteger(targetIndex)) return;
            if (targetIndex < 0 || targetIndex >= this.items.length) return;

            this.$nextTick(() => {
                if (!this.emblaApi) return;
                this.emblaApi.scrollTo(targetIndex);
                this.$emit('gallery-item-focus-applied', targetIndex);
            });
        },
        updateCarouselState() {
            if (!this.emblaApi) return;
            this.selectedScrollSnap = this.emblaApi.selectedScrollSnap();
            this.scrollSnaps = this.emblaApi.scrollSnapList();
            this.cacheLoadedImageRatios();
        },
        scrollPrevious() {
            this.emblaApi?.scrollPrev();
        },
        scrollNext() {
            this.emblaApi?.scrollNext();
        },
        scrollTo(index) {
            this.emblaApi?.scrollTo(index);
        },
        handleCarouselImageLoad(event) {
            this.storeImageRatio(event.target);
        },
        cacheLoadedImageRatios() {
            this.$nextTick(() => {
                const images = this.$refs.emblaViewport?.querySelectorAll(".gallery-carousel__slide img") || [];
                images.forEach((image) => {
                    if (image.complete) {
                        this.storeImageRatio(image);
                    }
                });
            });
        },
        storeImageRatio(image) {
            if (image?.tagName !== "IMG") return;

            const originalIndex = Number(image.closest(".gallery-carousel__slide")?.dataset?.originalIndex);
            const width = Number(image.naturalWidth);
            const height = Number(image.naturalHeight);

            if (!Number.isInteger(originalIndex) || !width || !height) {
                return;
            }

            this.originalImageRatios = {
                ...this.originalImageRatios,
                [originalIndex]: width / height
            };
        },
        getOriginalAspectRatioColumnSpan(index, media) {
            const storedRatio = this.originalImageRatios[index];
            if (storedRatio) {
                return this.getColumnSpanForAspectRatio(storedRatio);
            }

            const width = Number(media?.width);
            const height = Number(media?.height);

            if (!width || !height) {
                return 4;
            }

            return this.getColumnSpanForAspectRatio(width / height);
        },
        getColumnSpanForAspectRatio(ratio) {
            if (ratio >= 1.2) return 8;
            if (ratio >= 0.9) return 6;
            if (ratio >= 0.65) return 5;
            return 4;
        }
    }
}
</script>

<style scoped lang="scss">
@import "@/styles/components/sections/modules/gallery_carousel.scss";
.gallery-layout-module--carousel {
    --UIControlContainerBgColor: rgba(255, 255, 255, 0.70);
}
</style>
